import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Icon from '../components/ui/Icon';
import Button from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { EmptyState, ErrorState } from '../components/ui/States';
import TierCards from '../components/coupons/TierCards';
import CouponsTable from '../components/coupons/CouponsTable';
import CouponDrawer from '../components/coupons/CouponDrawer';
import { StatusFilterChips } from '../components/coupons/PackageStatus';
import useCoupons, { couponsQueryKey } from '../components/coupons/useCoupons';
import { levelCount } from '../components/coupons/couponAdapter';
import { toForm, toPayload } from '../components/coupons/couponForm';
import { DEFAULT_PARAMS, MAX_SEARCH_LENGTH } from '../components/coupons/couponsParams';
import { couponsApi } from '../api/coupons';
import { ApiError } from '../api/client';

const LIMIT_OPTIONS = [10, 20, 50, 100];
const ACTIVE_PARAMS = { ...DEFAULT_PARAMS, limit: 1, status: 'active' };

const errorMessage = (err) => (err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');

function errorView(error) {
  if (error?.status === 403) return { icon: 'lock', title: 'Admin access required', retry: false };
  if (error?.status === 429) return { icon: 'hourglass_top', title: 'Too many requests, try again shortly', retry: true, retryAfter: 5 };
  return { icon: 'cloud_off', title: "Couldn't load coupon packages", description: error?.message, retry: true };
}

/** Reward Coupons & Level Tiers: list, view, add, edit, duplicate, activate and delete coupon packages. */
export default function CouponsPage() {
  const queryClient = useQueryClient();
  const {
    params,
    setParams,
    searchInput,
    setSearchInput,
    clearFilters,
    coupons,
    tiers,
    brands,
    statusCounts,
    pagination,
    isInitialLoading,
    isRefreshing,
    error,
    refetch,
  } = useCoupons();

  // The package app users see now, whatever the filters (for "#id will be archived")
  const activeQuery = useQuery({
    queryKey: couponsQueryKey(ACTIVE_PARAMS),
    queryFn: ({ signal }) => couponsApi.getCouponPackages(ACTIVE_PARAMS, { signal }),
    staleTime: 30_000,
  });
  const activePackage = activeQuery.data?.coupons[0] ?? coupons.find((c) => c.status === 'active') ?? null;

  /** @type {[{ mode: 'add'|'edit'|'view'|'duplicate', id?: number } | null, Function]} */
  const [drawer, setDrawer] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activateTarget, setActivateTarget] = useState(null);

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['coupons'] });
    queryClient.invalidateQueries({ queryKey: ['coupon'] });
  };

  const remove = useMutation({
    mutationFn: (id) => couponsApi.deleteCouponPackage(id),
    onSuccess: ({ message, id }) => {
      toast.success(message || `Coupon package #${id} deleted`);
      setDeleteTarget(null);
      if (drawer?.id === id) setDrawer(null);
      queryClient.removeQueries({ queryKey: ['coupon', id] });
      // If that was the last package on this page, useCoupons moves to the previous page
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
    },
    onError: (err) => {
      // 409 (active package), 404, … → the server's own message
      toast.error(errorMessage(err));
      setDeleteTarget(null);
    },
  });

  // PUT needs the full package, so load the latest version and send it back with status active
  const activate = useMutation({
    mutationFn: async (id) => {
      const pkg = await queryClient.fetchQuery({
        queryKey: ['coupon', id],
        queryFn: ({ signal }) => couponsApi.getCouponPackage(id, { signal }),
        staleTime: 0,
      });
      return couponsApi.updateCouponPackage(id, { ...toPayload(toForm(pkg, levelCount(tiers, pkg))), status: 'active' });
    },
    onSuccess: ({ message, coupon }) => {
      toast.success(message || `Coupon package #${coupon.id} is now active`);
      setActivateTarget(null);
      refreshAll();
    },
    onError: (err) => {
      toast.error(errorMessage(err));
      setActivateTarget(null);
    },
  });

  const hasFilters = Boolean(params.search || params.brand || params.status !== 'all');
  const total = pagination?.totalItems ?? 0;
  // Keep the selected brand in the list even if it's no longer returned
  const brandOptions = params.brand && !brands.includes(params.brand) ? [params.brand, ...brands] : brands;
  const openAdd = () => setDrawer({ mode: 'add' });

  let body;
  if (error && !isInitialLoading) {
    const view = errorView(error);
    body = (
      <ErrorState
        icon={view.icon}
        title={view.title}
        description={view.description}
        retryAfter={view.retryAfter}
        onRetry={view.retry ? () => refetch() : undefined}
      />
    );
  } else if (!isInitialLoading && pagination && coupons.length === 0) {
    body = (
      <EmptyState
        tone="neutral"
        icon="confirmation_number"
        title="No coupon packages found"
        description={hasFilters ? 'Nothing matches the current search, brand or status filter.' : 'Create the first package to define milestone rewards.'}
        action={
          <div className="flex flex-wrap justify-center gap-2">
            <Button icon="add_circle" onClick={openAdd}>
              Add package
            </Button>
            {hasFilters && (
              <Button variant="secondary" icon="filter_alt_off" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>
        }
      />
    );
  } else {
    body = (
      <CouponsTable
        coupons={coupons}
        tiers={tiers}
        loading={isInitialLoading}
        refreshing={isRefreshing}
        onView={(c) => setDrawer({ mode: 'view', id: c.id })}
        onEdit={(c) => setDrawer({ mode: 'edit', id: c.id })}
        onDuplicate={(c) => setDrawer({ mode: 'duplicate', id: c.id })}
        onMakeActive={(c) => setActivateTarget(c)}
        onDelete={(c) => setDeleteTarget(c)}
      />
    );
  }

  const deleteCount = deleteTarget ? deleteTarget.levels.length : 0;
  const replacing = activePackage && activePackage.id !== activateTarget?.id ? activePackage.id : null;

  return (
    <>
      <section className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-primary tracking-tight">Reward Coupons &amp; Level Tiers</h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            Manage milestone coupon packages, partner brands, and voucher details for driver scan targets. App users see the active package.
          </p>
        </div>
        <Button size="lg" icon="add_circle" onClick={openAdd} className="shrink-0">
          Add New Coupon Package
        </Button>
      </section>

      <TierCards tiers={tiers} loading={isInitialLoading} />

      <section className="bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-xs p-3 flex flex-col gap-3">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1" role="search">
            <label htmlFor="coupons-search" className="sr-only">
              Search coupon packages
            </label>
            <Icon name="search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none" />
            <input
              id="coupons-search"
              type="search"
              autoComplete="off"
              maxLength={MAX_SEARCH_LENGTH}
              placeholder="Search by ID, package name, brand, offer or coupon code…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full h-10 pl-9 pr-3 text-[13px] bg-surface-container-lowest border border-outline-variant/50 rounded-lg text-on-surface placeholder:text-on-surface-variant/60 outline-none transition focus:border-primary-container focus:shadow-[0_0_0_3px_rgba(34,197,94,0.15)]"
            />
          </div>
          <div className="relative md:w-56">
            <label htmlFor="coupons-brand" className="sr-only">
              Brand partner
            </label>
            <select
              id="coupons-brand"
              value={params.brand}
              onChange={(e) => setParams({ brand: e.target.value })}
              className="w-full h-10 appearance-none pl-3 pr-8 bg-surface-container-low text-[13px] text-on-surface rounded-lg border border-transparent outline-none focus:border-primary-container focus:shadow-[0_0_0_3px_rgba(34,197,94,0.15)]"
            >
              <option value="">All Brand Partners</option>
              {brandOptions.map((brand) => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </select>
            <Icon name="expand_more" size={18} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-on-surface-variant" />
          </div>
        </div>
        <StatusFilterChips value={params.status} counts={statusCounts} onChange={(status) => setParams({ status })} />
      </section>

      <section className="relative bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-xs overflow-hidden">
        <p className="sr-only" role="status" aria-live="polite">
          {isInitialLoading || isRefreshing || !pagination ? '' : `${total} coupon ${total === 1 ? 'package' : 'packages'} found`}
        </p>
        {isRefreshing && (
          <div className="absolute top-2 right-3 z-20 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-surface-container-lowest/90 shadow-xs text-[11px] text-on-surface-variant">
            <Icon name="progress_activity" size={14} className="animate-spin text-secondary" />
            Loading…
          </div>
        )}
        {body}
        {!error && pagination && total > 0 && (
          <Pagination
            page={params.page}
            pageSize={params.limit}
            total={total}
            onPageChange={(page) => setParams({ page })}
            pageSizeOptions={LIMIT_OPTIONS}
            onPageSizeChange={(limit) => setParams({ limit })}
            disabled={isRefreshing}
            summary={
              <span>
                Displaying <strong className="font-data-mono text-on-surface">{coupons.length}</strong> of{' '}
                <strong className="font-data-mono text-on-surface">{total}</strong> coupon {total === 1 ? 'package' : 'packages'}
              </span>
            }
          />
        )}
      </section>

      {drawer && (
        <CouponDrawer
          key={`${drawer.mode}-${drawer.id ?? 'new'}`}
          mode={drawer.mode}
          couponId={drawer.id}
          tiers={tiers}
          brands={brands}
          activeId={activePackage?.id ?? null}
          onClose={() => setDrawer(null)}
          onEdit={(id) => setDrawer({ mode: 'edit', id })}
          onDelete={(c) => setDeleteTarget(c)}
        />
      )}

      <ConfirmDialog
        open={Boolean(activateTarget)}
        tone="primary"
        title={`Make package #${activateTarget?.id} active?`}
        description={`App users will see these rewards immediately.${replacing ? ` Package #${replacing} will be archived.` : ''}`}
        confirmLabel="Make active"
        busy={activate.isPending}
        onConfirm={() => activate.mutate(activateTarget.id)}
        onCancel={() => setActivateTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`Delete package #${deleteTarget?.id} and its ${deleteCount} ${deleteCount === 1 ? 'coupon' : 'coupons'}?`}
        description="This can't be undone."
        confirmLabel="Delete package"
        busy={remove.isPending}
        onConfirm={() => remove.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
